import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Fab,
  Paper,
  Tooltip,
} from '@mui/material';
import { Add, Edit, Timeline, CalendarToday, Close } from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { useSnackbar } from 'notistack';
import { apiGet, apiPost, apiPut } from '../services/apiClient';
import { LoggerService } from '../services/LoggerService';

interface Fund {
  id: string;
  name: string;
}

interface ShareClass {
  id: string;
  fund_id: string;
  name: string;
  class_type: string;
  currency: string;
  isin: string;
  status: string;
  nav_per_share: number | null;
  management_fee_bps: number;
  performance_fee_bps: number;
  entry_fee_bps: number;
  exit_fee_bps: number;
  minimum_initial_investment: number;
  dealing_frequency: string;
  settlement_cycle_days: number;
  launch_date: string;
  created_at: string;
  updated_at: string;
}

interface NavHistoryEntry {
  date: string;
  nav_per_share: number;
}

interface DealingDay {
  date: string;
  type: string;
  cutoff_time: string;
}

interface ShareClassFormData {
  name: string;
  class_type: string;
  currency: string;
  isin: string;
  management_fee_bps: number;
  performance_fee_bps: number;
  entry_fee_bps: number;
  exit_fee_bps: number;
  minimum_initial_investment: number;
  dealing_frequency: string;
  settlement_cycle_days: number;
  launch_date: string;
}

const INITIAL_FORM_DATA: ShareClassFormData = {
  name: '',
  class_type: 'institutional',
  currency: 'EUR',
  isin: '',
  management_fee_bps: 0,
  performance_fee_bps: 0,
  entry_fee_bps: 0,
  exit_fee_bps: 0,
  minimum_initial_investment: 0,
  dealing_frequency: 'daily',
  settlement_cycle_days: 3,
  launch_date: '',
};

const CLASS_TYPES = [
  { value: 'institutional', label: 'Institutional' },
  { value: 'retail', label: 'Retail' },
  { value: 'accumulation', label: 'Accumulation' },
  { value: 'distribution', label: 'Distribution' },
  { value: 'hedged', label: 'Hedged' },
];

const CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF', 'JPY', 'SEK', 'NOK', 'DKK'];

const DEALING_FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi_weekly', label: 'Bi-Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

function getStatusChipColor(status: string): 'default' | 'success' | 'warning' | 'error' {
  switch (status) {
    case 'active':
      return 'success';
    case 'soft_closed':
      return 'warning';
    case 'hard_closed':
    case 'terminated':
      return 'error';
    case 'draft':
    default:
      return 'default';
  }
}

function formatCurrency(amount: number | null, currency: string): string {
  if (amount === null || amount === undefined) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(amount);
}

function formatBps(bps: number): string {
  return `${(bps / 100).toFixed(2)}%`;
}

function formatStatusLabel(status: string): string {
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

const ShareClassesPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [funds, setFunds] = useState<Fund[]>([]);
  const [selectedFundId, setSelectedFundId] = useState<string>('');
  const [shareClasses, setShareClasses] = useState<ShareClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [navDialogOpen, setNavDialogOpen] = useState(false);
  const [dealingDaysDialogOpen, setDealingDaysDialogOpen] = useState(false);

  const [formData, setFormData] = useState<ShareClassFormData>(INITIAL_FORM_DATA);
  const [editingShareClass, setEditingShareClass] = useState<ShareClass | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);

  const [navHistory, setNavHistory] = useState<NavHistoryEntry[]>([]);
  const [navLoading, setNavLoading] = useState(false);
  const [navShareClassName, setNavShareClassName] = useState('');

  const [dealingDays, setDealingDays] = useState<DealingDay[]>([]);
  const [dealingDaysLoading, setDealingDaysLoading] = useState(false);
  const [dealingDaysShareClassName, setDealingDaysShareClassName] = useState('');

  useEffect(() => {
    loadFunds();
  }, []);

  useEffect(() => {
    if (selectedFundId) {
      loadShareClasses(selectedFundId);
    } else {
      setShareClasses([]);
    }
  }, [selectedFundId]);

  const loadFunds = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiGet<{ funds: Fund[] }>('/funds/');
      setFunds(response.funds || []);
      LoggerService.info('Funds loaded successfully', { count: (response.funds || []).length });
    } catch (err: any) {
      const message = err.message || 'Failed to load funds';
      LoggerService.error('Error loading funds:', err);
      setError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadShareClasses = async (fundId: string) => {
    try {
      setTableLoading(true);
      setError(null);
      const response = await apiGet<ShareClass[]>(`/funds/${fundId}/share-classes`);
      const classes = Array.isArray(response) ? response : [];
      setShareClasses(classes);
      LoggerService.info('Share classes loaded', { fundId, count: classes.length });
    } catch (err: any) {
      const message = err.message || 'Failed to load share classes';
      LoggerService.error('Error loading share classes:', err);
      setError(message);
      enqueueSnackbar(message, { variant: 'error' });
      setShareClasses([]);
    } finally {
      setTableLoading(false);
    }
  };

  const handleCreateOpen = () => {
    setFormData(INITIAL_FORM_DATA);
    setCreateDialogOpen(true);
  };

  const handleCreateClose = () => {
    setCreateDialogOpen(false);
    setFormData(INITIAL_FORM_DATA);
  };

  const handleCreateSubmit = async () => {
    if (!selectedFundId) {
      enqueueSnackbar('Please select a fund first', { variant: 'warning' });
      return;
    }
    if (!formData.name.trim()) {
      enqueueSnackbar('Share class name is required', { variant: 'warning' });
      return;
    }
    try {
      setFormSubmitting(true);
      await apiPost(`/funds/${selectedFundId}/share-classes`, formData);
      enqueueSnackbar('Share class created successfully', { variant: 'success' });
      handleCreateClose();
      await loadShareClasses(selectedFundId);
      LoggerService.info('Share class created', { fundId: selectedFundId, name: formData.name });
    } catch (err: any) {
      const message = err.message || 'Failed to create share class';
      LoggerService.error('Error creating share class:', err);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleEditOpen = (shareClass: ShareClass) => {
    setEditingShareClass(shareClass);
    setFormData({
      name: shareClass.name,
      class_type: shareClass.class_type,
      currency: shareClass.currency,
      isin: shareClass.isin,
      management_fee_bps: shareClass.management_fee_bps,
      performance_fee_bps: shareClass.performance_fee_bps,
      entry_fee_bps: shareClass.entry_fee_bps,
      exit_fee_bps: shareClass.exit_fee_bps,
      minimum_initial_investment: shareClass.minimum_initial_investment,
      dealing_frequency: shareClass.dealing_frequency,
      settlement_cycle_days: shareClass.settlement_cycle_days,
      launch_date: shareClass.launch_date,
    });
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditingShareClass(null);
    setFormData(INITIAL_FORM_DATA);
  };

  const handleEditSubmit = async () => {
    if (!editingShareClass) return;
    if (!formData.name.trim()) {
      enqueueSnackbar('Share class name is required', { variant: 'warning' });
      return;
    }
    try {
      setFormSubmitting(true);
      await apiPut(`/share-classes/${editingShareClass.id}`, formData);
      enqueueSnackbar('Share class updated successfully', { variant: 'success' });
      handleEditClose();
      if (selectedFundId) {
        await loadShareClasses(selectedFundId);
      }
      LoggerService.info('Share class updated', { id: editingShareClass.id, name: formData.name });
    } catch (err: any) {
      const message = err.message || 'Failed to update share class';
      LoggerService.error('Error updating share class:', err);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleViewNavHistory = async (shareClass: ShareClass) => {
    setNavShareClassName(shareClass.name);
    setNavDialogOpen(true);
    setNavLoading(true);
    try {
      const response = await apiGet<NavHistoryEntry[]>(`/share-classes/${shareClass.id}/nav-history`);
      const history = Array.isArray(response) ? response : [];
      setNavHistory(history);
      LoggerService.info('NAV history loaded', { shareClassId: shareClass.id, entries: history.length });
    } catch (err: any) {
      LoggerService.error('Error loading NAV history:', err);
      enqueueSnackbar(err.message || 'Failed to load NAV history', { variant: 'error' });
      setNavHistory([]);
    } finally {
      setNavLoading(false);
    }
  };

  const handleViewDealingDays = async (shareClass: ShareClass) => {
    setDealingDaysShareClassName(shareClass.name);
    setDealingDaysDialogOpen(true);
    setDealingDaysLoading(true);
    try {
      const response = await apiGet<DealingDay[]>(`/share-classes/${shareClass.id}/dealing-days`);
      const days = Array.isArray(response) ? response : [];
      setDealingDays(days);
      LoggerService.info('Dealing days loaded', { shareClassId: shareClass.id, count: days.length });
    } catch (err: any) {
      LoggerService.error('Error loading dealing days:', err);
      enqueueSnackbar(err.message || 'Failed to load dealing days', { variant: 'error' });
      setDealingDays([]);
    } finally {
      setDealingDaysLoading(false);
    }
  };

  const handleFormChange = (field: keyof ShareClassFormData, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const renderShareClassForm = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Name"
          value={formData.name}
          onChange={(e) => handleFormChange('name', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Class Type</InputLabel>
          <Select
            value={formData.class_type}
            label="Class Type"
            onChange={(e) => handleFormChange('class_type', e.target.value)}
          >
            {CLASS_TYPES.map((ct) => (
              <MenuItem key={ct.value} value={ct.value}>
                {ct.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Currency</InputLabel>
          <Select
            value={formData.currency}
            label="Currency"
            onChange={(e) => handleFormChange('currency', e.target.value)}
          >
            {CURRENCIES.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="ISIN"
          value={formData.isin}
          onChange={(e) => handleFormChange('isin', e.target.value)}
          inputProps={{ maxLength: 12 }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Management Fee (bps)"
          type="number"
          value={formData.management_fee_bps}
          onChange={(e) => handleFormChange('management_fee_bps', Number(e.target.value))}
          inputProps={{ min: 0 }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Performance Fee (bps)"
          type="number"
          value={formData.performance_fee_bps}
          onChange={(e) => handleFormChange('performance_fee_bps', Number(e.target.value))}
          inputProps={{ min: 0 }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Entry Fee (bps)"
          type="number"
          value={formData.entry_fee_bps}
          onChange={(e) => handleFormChange('entry_fee_bps', Number(e.target.value))}
          inputProps={{ min: 0 }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Exit Fee (bps)"
          type="number"
          value={formData.exit_fee_bps}
          onChange={(e) => handleFormChange('exit_fee_bps', Number(e.target.value))}
          inputProps={{ min: 0 }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Minimum Initial Investment"
          type="number"
          value={formData.minimum_initial_investment}
          onChange={(e) => handleFormChange('minimum_initial_investment', Number(e.target.value))}
          inputProps={{ min: 0 }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Dealing Frequency</InputLabel>
          <Select
            value={formData.dealing_frequency}
            label="Dealing Frequency"
            onChange={(e) => handleFormChange('dealing_frequency', e.target.value)}
          >
            {DEALING_FREQUENCIES.map((df) => (
              <MenuItem key={df.value} value={df.value}>
                {df.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Settlement Cycle (days)"
          type="number"
          value={formData.settlement_cycle_days}
          onChange={(e) => handleFormChange('settlement_cycle_days', Number(e.target.value))}
          inputProps={{ min: 1, max: 10 }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Launch Date"
          type="date"
          value={formData.launch_date}
          onChange={(e) => handleFormChange('launch_date', e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
    </Grid>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading Share Classes...
        </Typography>
      </Box>
    );
  }

  if (error && funds.length === 0) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Share Classes
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" sx={{ mt: 2 }} onClick={loadFunds}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Share Classes
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage share classes, view NAV history, and track dealing schedules across your funds
        </Typography>
      </Box>

      {/* Fund Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Select Fund</InputLabel>
                <Select
                  value={selectedFundId}
                  label="Select Fund"
                  onChange={(e) => setSelectedFundId(e.target.value)}
                >
                  {funds.map((fund) => (
                    <MenuItem key={fund.id} value={fund.id}>
                      {fund.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleCreateOpen}
                  disabled={!selectedFundId}
                >
                  Add Share Class
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Share Classes Table */}
      {!selectedFundId ? (
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="center" alignItems="center" py={6}>
              <Typography variant="h6" color="text.secondary">
                Select a fund to view its share classes
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : tableLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress size={40} />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading share classes...
          </Typography>
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : shareClasses.length === 0 ? (
        <Card>
          <CardContent>
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" py={6}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No share classes found for this fund
              </Typography>
              <Button variant="outlined" startIcon={<Add />} onClick={handleCreateOpen} sx={{ mt: 2 }}>
                Create First Share Class
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Currency</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>ISIN</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">NAV</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Mgmt Fee</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Dealing Freq</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shareClasses.map((sc) => (
                <TableRow key={sc.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {sc.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {sc.class_type}
                    </Typography>
                  </TableCell>
                  <TableCell>{sc.currency}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {sc.isin || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={formatStatusLabel(sc.status)}
                      color={getStatusChipColor(sc.status)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatCurrency(sc.nav_per_share, sc.currency)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {formatBps(sc.management_fee_bps)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {sc.dealing_frequency.replace('_', '-')}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEditOpen(sc)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="NAV History">
                      <IconButton size="small" onClick={() => handleViewNavHistory(sc)}>
                        <Timeline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Dealing Days">
                      <IconButton size="small" onClick={() => handleViewDealingDays(sc)}>
                        <CalendarToday fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Floating Action Button (visible when fund selected and classes exist) */}
      {selectedFundId && shareClasses.length > 0 && (
        <Fab
          color="primary"
          aria-label="add share class"
          onClick={handleCreateOpen}
          sx={{ position: 'fixed', bottom: 24, right: 24 }}
        >
          <Add />
        </Fab>
      )}

      {/* Create Share Class Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCreateClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Create Share Class</Typography>
            <IconButton onClick={handleCreateClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>{renderShareClassForm()}</DialogContent>
        <DialogActions>
          <Button onClick={handleCreateClose} disabled={formSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateSubmit}
            disabled={formSubmitting || !formData.name.trim()}
          >
            {formSubmitting ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Share Class Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Edit Share Class: {editingShareClass?.name}
            </Typography>
            <IconButton onClick={handleEditClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>{renderShareClassForm()}</DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose} disabled={formSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditSubmit}
            disabled={formSubmitting || !formData.name.trim()}
          >
            {formSubmitting ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* NAV History Dialog */}
      <Dialog open={navDialogOpen} onClose={() => setNavDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">NAV History: {navShareClassName}</Typography>
            <IconButton onClick={() => setNavDialogOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {navLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={6}>
              <CircularProgress size={40} />
              <Typography variant="body1" sx={{ ml: 2 }}>
                Loading NAV history...
              </Typography>
            </Box>
          ) : navHistory.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={6}>
              <Typography variant="body1" color="text.secondary">
                No NAV history available for this share class
              </Typography>
            </Box>
          ) : (
            <Box sx={{ width: '100%', height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={navHistory} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(value: string) => {
                      const d = new Date(value);
                      return `${d.getMonth() + 1}/${d.getDate()}`;
                    }}
                  />
                  <YAxis
                    domain={['auto', 'auto']}
                    tickFormatter={(value: number) => value.toFixed(2)}
                  />
                  <RechartsTooltip
                    formatter={(value: number) => [value.toFixed(4), 'NAV per Share']}
                    labelFormatter={(label: string) => `Date: ${label}`}
                  />
                  <Line
                    type="monotone"
                    dataKey="nav_per_share"
                    stroke="#1976d2"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNavDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Dealing Days Dialog */}
      <Dialog
        open={dealingDaysDialogOpen}
        onClose={() => setDealingDaysDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Upcoming Dealing Days: {dealingDaysShareClassName}</Typography>
            <IconButton onClick={() => setDealingDaysDialogOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {dealingDaysLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={6}>
              <CircularProgress size={40} />
              <Typography variant="body1" sx={{ ml: 2 }}>
                Loading dealing days...
              </Typography>
            </Box>
          ) : dealingDays.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={6}>
              <Typography variant="body1" color="text.secondary">
                No upcoming dealing days available
              </Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Cutoff Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dealingDays.map((day, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      {new Date(day.date).toLocaleDateString('en-GB', {
                        weekday: 'short',
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={day.type}
                        size="small"
                        color={day.type === 'subscription' ? 'success' : 'warning'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{day.cutoff_time}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDealingDaysDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShareClassesPage;
